// One set of KYC rules for every place Ops sees a publisher — the cross-brand list,
// a brand's Publishers tab and the publisher page — so an action never shows in one
// and not the other.

export const KYC_EXPIRY_WARNING_DAYS = 5;

export const isKycVerified = (publisher) => publisher?.kyc_status === "verified";

// Anyone not yet verified can be nudged — including a rejected publisher, who has to redo it.
export const canRemindKyc = (publisher) => Boolean(publisher) && !isKycVerified(publisher);

// Force verify overrides a check that is waiting, stalled or failed — there's nothing
// to override for a publisher who never started.
export const canForceVerifyKyc = (publisher) => ["pending", "in_progress", "rejected"].includes(publisher?.kyc_status);

// The list endpoints send `kyc_window_days_left`; the KYC endpoint sends `days_remaining`.
export const kycDaysLeft = (publisher) => {
    const days = publisher?.days_remaining ?? publisher?.kyc_window_days_left;
    return !isKycVerified(publisher) && Number.isFinite(days) ? days : null;
};

export const isKycExpiringSoon = (publisher) => {
    const days = kycDaysLeft(publisher);
    return days !== null && days <= KYC_EXPIRY_WARNING_DAYS;
};

// The reminder is sent on behalf of a brand. A brand's tab knows which one; the
// cross-brand list only has it if the row carries `account_id`.
export const brandOfPublisher = (publisher, fallbackBrand) => ({
    id  : publisher?.account_id || fallbackBrand?.id,
    name: publisher?.brand || publisher?.account_name || fallbackBrand?.name
});

// Drops brands without an id and repeats, keeping the first (most specific) one.
export const uniqueBrands = (brands) => brands
    .filter((brand) => brand?.id)
    .filter((brand, index, list) => list.findIndex((other) => other.id === brand.id) === index);

// The two list endpoints name the pending figure differently.
export const toReminderRecipient = (publisher) => ({
    name          : publisher.holder_name,
    email         : publisher.email,
    pending_amount: publisher.earnings_pending ?? publisher.pending_amount
});

// Mirrors what the publisher's KYC wizard stores in each document
// (publisher/kyc/use-publisher-kyc.js). `required` fields are always listed once
// the document is in, so a gap shows as "Missing" instead of silently vanishing.
// GST is skippable in the wizard, so it never counts towards completeness.
export const KYC_DOCS = [
    {
        key   : "pan_card",
        label : "PAN",
        fields: [
            // { key: "number", label: "PAN number", required: true, copyable: true },
            { key: "name", label: "Name on PAN", required: true }
        ]
    },
    {
        key   : "bank_account",
        label : "Bank account",
        fields: [
            // { key: "account_number", label: "Account number", required: true, copyable: true },
            { key: "ifsc_code", label: "IFSC code", required: true, copyable: true },
            { key: "account_holder_name", label: "Account holder name", required: true },
            { key: "bank_name", label: "Bank" }
        ]
    },
    {
        key     : "gst_number",
        label   : "GST",
        optional: true,
        fields  : [{ key: "number", label: "GSTIN", required: true, copyable: true }]
    }
];

// One line under the status chip on the KYC tab, saying what the status means for Ops.
export const KYC_STATUS_HINT = {
    not_started: "The publisher hasn't started KYC yet. Payouts stay on hold until they do.",
    pending    : "Documents are submitted and waiting to be verified. Force verify once you've checked them.",
    in_progress: "KYC is started but not finished. Nudge them, or force verify once you've checked the documents.",
    rejected   : "Verification failed. Ask the publisher to resubmit, or force verify after checking the documents yourself.",
    verified   : "KYC is complete — payouts can be released."
};

const normalizeName = (name) => (name || "").toLowerCase().replace(/[^a-z]/g, "");

const toDocView = (doc, entry) => ({
    key         : doc.key,
    label       : doc.label,
    optional    : Boolean(doc.optional),
    status      : entry?.status,
    originalName: entry?.original_name,
    fields      : entry
        ? doc.fields.filter((field) => field.required || entry[field.key]).map((field) => ({ ...field, value: entry[field.key] }))
        : []
});

// Everything the KYC tab renders, derived once from the merged publisher record.
export const toKycSummary = (publisher) => {
    const docsById = publisher.kyc_docs || {};
    const docs = KYC_DOCS.map((doc) => toDocView(doc, docsById[doc.key]));
    const requiredDocs = docs.filter((doc) => !doc.optional);
    const daysLeft = kycDaysLeft(publisher);
    const windowDays = publisher.kyc_window_days;
    const panName = normalizeName(docsById.pan_card?.name);
    const holderName = normalizeName(docsById.bank_account?.account_holder_name);

    return {
        status       : publisher.kyc_status,
        hint         : KYC_STATUS_HINT[publisher.kyc_status],
        daysLeft,
        expiring     : isKycExpiringSoon(publisher),
        windowDays,
        windowUsedPct: windowDays > 0 && daysLeft !== null
            ? Math.min(100, Math.max(0, Math.round(((windowDays - daysLeft) / windowDays) * 100)))
            : null,
        docs,
        requiredCount : requiredDocs.length,
        submittedCount: requiredDocs.filter((doc) => doc.status).length,
        // Worth a second look before a force verify — payouts go to the bank account holder.
        nameMismatch  : Boolean(panName && holderName && panName !== holderName),
        canRemind     : canRemindKyc(publisher),
        canForceVerify: canForceVerifyKyc(publisher)
    };
};
